import React from "react";

import { GeoJSONSource, Map, Popup, StyleSpecification } from "maplibre-gl";
import 'maplibre-gl/src/css/maplibre-gl.css'

import { zip, torsToLayers, addLayerToMap, timeIsKnown, pairwise, colorBlend} from "../utils/utils";

import './MapWidget.css';
import { Partitioner } from "../utils/Partitioner";
import { MultiPlotLayer } from "autumnplot-gl";
import { SelectionInterval } from "../utils/SelectionInterval";
import TornadoCollection from "../utils/TorCollection";
import { DateTime, Duration, Interval } from "luxon";
import {EnvironmentDataInterface, EnvironmentLayerSpec, EnvironmentSource, LayerType, LAYER_KEY_FMT} from "../utils/EnvironmentDataHandler";
import { TornadoMin, TornadoGeoJSONMin, isTornadoFeatureCollection } from "../utils/tornado";
import { MapState } from "./Constants";
import style from '../json/style.json';

const TORNADO_NAMES: Record<string, string> = {
    //'SPC49749_NCEI35937': 'Greensburg',
}

interface MapWidgetProps {
    controls_visible: boolean
    interval: SelectionInterval | null;
    tor_coll: TornadoCollection | null;
    partitioner: Partitioner;

    env_data_times: DateTime[];
    sel_data_time: DateTime | null;
    env_handler: EnvironmentDataInterface | null;
    env_type: LayerType | null;
    reanalysis_domain: string;
    reanalysis_src: EnvironmentSource | null;

    cday_offset: Duration;
    units: string;
    fit_bounds: [[number, number], [number, number]];
    map_position: MapState | null;

    setscreenshotfunc: (ss_func: () => Promise<HTMLCanvasElement>, which: string) => void;

    onmove: (new_info: MapState) => void;
    onmapready: () => void;
    onmapnotready?: () => void;
    onloadingstart: (what: string) => void;
    onloadingend: (what: string) => void;
    onintervalchange: (intv: SelectionInterval, recenter_timeline?: boolean) => void;
}

class MapWidget extends React.Component<MapWidgetProps> {
    partitioner: Partitioner;
    map: Map | null;

    is_ready: boolean;
    is_loaded: boolean;
    env_layers: {spec: EnvironmentLayerSpec, layer: MultiPlotLayer}[];
    mapContainer: HTMLDivElement | null;

    constructor(props: MapWidgetProps) {
        super(props);
        this.state = {};

        this.partitioner = props['partitioner'];

        this.map = null;

        this.addTornadoes = this.addTornadoes.bind(this);
        this.addEnvironmentData = this.addEnvironmentData.bind(this);
        this.torsToLayers = this.torsToLayers.bind(this);

        this.is_ready = false;
        this.is_loaded = false;

        this.env_layers = [];
        this.mapContainer = null;
    }
    
    async componentDidUpdate(prev_props: MapWidgetProps) {
        if (this.map === null || this.mapContainer === null || !this.is_loaded) {
            return;
        }

        if (prev_props['controls_visible'] != this.props['controls_visible']) {
            if (this.props['controls_visible']) {
                this.mapContainer.classList.remove('controls-hidden');
            }
            else {
                this.mapContainer.classList.add('controls-hidden');
            }
            this.map.resize();
        }

        const arrays_equal = (a: any[], b: any[]) => {
            return a.length == b.length && [...zip(a, b)].map(([ae, be]) => ae == be).reduce((a, b) => a && b, true);
        }

        if (this.props['env_type'] != prev_props['env_type'] || this.props['reanalysis_src'] != prev_props['reanalysis_src']) {
            this.addEnvironmentData();
            this.updateEnvironmentTimes();
        }
        else if (!arrays_equal(this.props['env_data_times'], prev_props['env_data_times'])) {
            if (prev_props['sel_data_time'] === null || this.props['sel_data_time'] === null) {
                this.addEnvironmentData();
            }
            this.updateEnvironmentTimes();
        }

        if (this.props['sel_data_time'] !== prev_props['sel_data_time']) {
            this.updateSelectedTime();
        }

        if (this.props['fit_bounds'] != prev_props['fit_bounds']) {
            this._setViewport(this.props['fit_bounds'], false);
        }

        if ((this.props['interval'] === null && prev_props['interval'] === null 
            || this.props['interval'] !== null && prev_props['interval'] !== null && this.props['interval'].equals(prev_props['interval']))
            && this.props['tor_coll'] === prev_props['tor_coll']) {
            return;
        }

        if (this.props['onloadingstart']) this.props['onloadingstart']('drawing_tors');
        var layers = await this.torsToLayers();

        const map = this.map;
        // Set data for each layer on the map (much cheaper than destroying and remaking each layer)
        Object.entries(layers).forEach(([layer_id, layer_json]) => {
            const lyr = map.getSource(layer_id);
            if (lyr === undefined || lyr.type != 'geojson') {
                throw `Layer '${layer_id}' could not be found or is not the right type`;
            }

            // Not sure why the above isn't triggering the type narrowing
            (lyr as GeoJSONSource).setData(layer_json);
        });

        if (this.props['onloadingend']) this.props['onloadingend']('drawing_tors');
    }

    componentDidMount() {
        if (this.mapContainer === null || this.map !== null) {
            // Component has already mounted
            return;
        }

        this.map = new Map({
            container: this.mapContainer,
            style: style as unknown as StyleSpecification,
            attributionControl: {compact: false},
    
            maxZoom: 14,
            //antialias: true, // Antialiasing kills performance on Safari
        });

        if (!this.props['controls_visible']) {
            this.mapContainer.classList.add('controls-hidden');
        }

        this.map.on('move', () => {
            if (this.map === null) return;

            const lon = this.map.getCenter().lng;
            const lat = this.map.getCenter().lat;
            const zoom = this.map.getZoom();

            if (this.props['onmove'] !== undefined) {
                this.props['onmove']({'center_lat': lat, 'center_lon': lon, 'zoom': zoom});
            }
        });

        if (this.props['map_position'] === null) {
            this._setViewport(this.props['fit_bounds'], true);
        }
        else {
            const map_pos = this.props['map_position'];
            this.map.setZoom(map_pos['zoom'], {'animate': false});
            this.map.setCenter([map_pos['center_lon'], map_pos['center_lat']], {'animate': false});            
        }
    
        this.map.on('error', event => {});

        this.map.on('load', () => this.is_loaded = true)
        this.map.on('load', this.addTornadoes);
        this.map.on('load', () => {
            this.addEnvironmentData();
            this.updateEnvironmentTimes();
        });
        this.map.on('load', () => this.updateSelectedTime());

        this.map.on('idle', () => {
            this.is_ready = true;
            if (this.props['onmapready'] !== undefined) {
                this.props['onmapready']();
            }
        });

        this.map.on('render', () => {
            if (this.is_ready) {
                if (this.props['onmapnotready'] !== undefined) {
                    this.props['onmapnotready']();
                }
            }
            this.is_ready = false;
        });

        this.props['setscreenshotfunc'](this.screenshot.bind(this), 'map');
    }

    addEnvironmentData() {
        if (this.map === null) return;

        const map = this.map;
        this.env_layers.map(layer => {
            if (map.getLayer(layer['layer'].id) !== undefined) {
                map.removeLayer(layer['layer'].id);
            }
        });

        const sel_data_time = this.props['sel_data_time'];
        if (this.props['env_type'] !== null && this.props['env_data_times'] !== null && sel_data_time !== null && this.props['env_handler'] !== null) {
            const layers = this.props['env_handler'].getLayers(this.props['env_type']);
        
            layers.map(layer => {
                layer['layer'].setActiveKey(sel_data_time.toFormat(LAYER_KEY_FMT));
                map.addLayer(layer['layer'], 'tunnel_railway_transit');
            });

            this.env_layers = layers;
        }
        else {
            this.env_layers = [];
        }
    }

    updateEnvironmentTimes() {
        if (this.props['env_data_times'] !== null && this.props['sel_data_time'] !== null && this.props['env_handler'] !== null) {
            this.props['env_handler'].populateLayers(this.env_layers, this.props['env_data_times'], this.props['sel_data_time']);
        }
    }

    updateSelectedTime() {
        const sel_data_time = this.props['sel_data_time'];
        if (sel_data_time !== null) {
            this.env_layers.forEach(layer => {
                layer['layer'].setActiveKey(sel_data_time.toFormat(LAYER_KEY_FMT));
            });
        }
    }

    _setViewport(bounds: [[number, number], [number, number]], is_initial: boolean) {
        if (this.map === null) return;

        this.map.setMinZoom(1);
        this.map.fitBounds(bounds,
            {'animate': !is_initial, 'padding': {'top': 55, 'bottom': 15, 'left': 15, 'right': 15}}
        );
    }

    async addTornadoes() {
        var layers = await this.torsToLayers();
        if (this.map === null) return;
        const map = this.map;

        const add_layer = ([layer_id, layer_json]: [string, GeoJSON.FeatureCollection]) => {
            addLayerToMap(map, layer_id, layer_json, this.partitioner);

            const prettysources = (sources: (string | Record<string, string | null>)[] | undefined) => {
                if (sources === undefined) {
                    return undefined;
                }

                let additional_idx = -1;
                let elems = sources.map((src_obj, idx) => {
                    const link_txt: string = Object.keys(src_obj)[0];
                    const link_href = Object.values(src_obj)[0];

                    if (link_txt == 'Source 1') {
                        additional_idx = idx;
                    }

                    return link_href === null ? link_txt : `<a key=${link_txt} href=${link_href} target="_blank">${link_txt}</a>`;
                });

                elems = elems.map((elem, idx) => idx == 0 ? elem : [', ', elem]).flat();
                if (additional_idx > 0) {
                    elems.splice(2 * additional_idx, 0, 'Additional: ');
                }
                return elems.join('');
            }

            // Set click handler for each tornado (creates a short table)
            map.on('click', layer_id, async event => {
                if (event.features === undefined || event.originalEvent.defaultPrevented) {
                    return;
                }

                const target_feature = event.features[0];
                if (target_feature['properties'] === null || !target_feature['properties']['popup'] || target_feature.source.includes('-polys')) {
                    return;
                }

                event.originalEvent.preventDefault();

                if (this.props['tor_coll'] === null) {
                    throw "Tornado collection is null in Map Widget";
                }

                const tor_full = await this.props['tor_coll'].getGeoJSONById(target_feature['properties']['id']);
                const tor_geom = isTornadoFeatureCollection(tor_full) ? tor_full.geometry.geometries[0] : tor_full.geometry;
                const tor_props = {...tor_full.properties} as Record<string, any>;
                if (isTornadoFeatureCollection(tor_full)) {
                    tor_props['f-scale'] = tor_props['f-scale'][0];
                }
                if (typeof tor_props['comments'] == 'string') {
                    tor_props['comments'] = {'': tor_props['comments']};
                }

                if (tor_geom.type == 'Polygon') {
                    return;
                }

                let coordinates: [number, number];
                if (tor_geom.type == 'Point') {
                    const coords_ = tor_geom.coordinates.slice();
                    coordinates = [coords_[0], coords_[1]];
                }
                else {
                    const tor_coords = tor_geom.coordinates.slice();
                    let path_length = [...pairwise(tor_coords)].map(([pt_a, pt_b]) => Math.hypot(pt_a[0] - pt_b[0], pt_a[1] - pt_b[1]));
                    let path_length_accum = [0];
                    path_length.forEach(seg => {
                        path_length_accum.push(path_length_accum[path_length_accum.length - 1] + seg);
                    });

                    const target_path_length = path_length_accum[path_length_accum.length - 1] / 2;
                    const target_idx = path_length_accum.indexOf([...pairwise(path_length_accum)]
                                                                    .filter(([a, b]) => a <= target_path_length && b >= target_path_length)[0][0]);

                    const pt1 = tor_coords[target_idx];
                    const pt2 = tor_coords[target_idx + 1];
                    let alpha;
                    if (path_length_accum[target_idx] == path_length_accum[target_idx + 1]) {
                        alpha = 0.5;
                    }
                    else {
                        alpha = (target_path_length - path_length_accum[target_idx]) 
                            / (path_length_accum[target_idx + 1] - path_length_accum[target_idx]);
                    }

                    coordinates = [(pt1[0] * (1 - alpha) + pt2[0] * alpha),
                                    (pt1[1] * (1 - alpha) + pt2[1] * alpha)];
                }

                const width_units = this.props['units'] == 'metric' ? 'm' : 'yds';
                const length_units = this.props['units'] == 'metric' ? 'km' : 'mi';

                const table: Record<string, {'header': string, 'is_prop': boolean, 'units': string}> = {
                    'datetime-start': {'header': 'Start Time', 'is_prop': true, 'units': ''},
                    'datetime-end': {'header': 'End Time', 'is_prop': true, 'units': ''},
                    'start-loc': {'header': 'Start Location', 'is_prop': false, 'units': ''},
                    'end-loc': {'header': 'End Location', 'is_prop': false, 'units': ''},
                    'f-scale': {'header': 'Rating', 'is_prop': true, 'units': ''},
                    'path-length': {'header': 'Path Length', 'is_prop': true, 'units': length_units},
                    'path-width': {'header': 'Path Width', 'is_prop': true, 'units': width_units},
                    'fatalities': {'header': 'Fatalities', 'is_prop': true, 'units': ''},
                    'injuries': {'header': 'Injuries', 'is_prop': true, 'units': ''},
                    'source': {'header': 'Source', 'is_prop': true, 'units': ''},
                };

                if (tor_props['comments'] !== null && tor_props['comments'] !== undefined) {
                    Object.keys(tor_props['comments']).forEach(k => {
                        const header = k != '' ? `Comments (From ${k})` : 'Comments';
                        table['comments_' + k] = {'header': header, 'is_prop': true, 'units': ''};
                    });
                }

                let table_rows = [];
                let header_fscale = "FU";

                for (let key in table) {
                    let info = table[key];
                    let header = info['header'];

                    let prop = undefined;
                    if (key == 'start-loc' || key == 'end-loc') {
                        const idx = key == 'start-loc' ? 0 : tor_geom.coordinates.length - 1;
                        let prop_: GeoJSON.Position = tor_geom['type'] == 'Point' ? tor_geom['coordinates'] : tor_geom['coordinates'][idx];
                        if (tor_geom['type'] == 'Point') {
                            if (key == 'start-loc') {
                                info['header'] = 'Location';
                            }
                            else {
                                continue;
                            }
                        }

                        let [lon, lat] = prop_.map(c => Math.round(c * 100) / 100);
                        const lon_str = lon < 0 ? -lon + '\u00b0 W' : lon + '\u00b0 E';
                        const lat_str = lat < 0 ? -lat + '\u00b0 S' : lat + '\u00b0 N';

                        prop = lat_str + ', ' + lon_str;
                    }
                    else {
                        prop = tor_props[key];
                        if (key == 'datetime-start') {
                            const dt = DateTime.fromFormat(prop, "yyyy-MM-dd'T'hh:mm:ss'Z'", {'zone': 'utc'});
    
                            const date = dt.toLocaleString(DateTime.DATE_MED)
                            const time = dt.toFormat('HHmm') + ' UTC ';
    
                            if (!timeIsKnown(tor_full['properties'])) {
                                prop = date;
                                header = 'Date';
                            }
                            else {
                                prop = time + " " + date;
                            }
                        }

                        if (key == 'datetime-end' && prop !== undefined) {
                            const dt = DateTime.fromFormat(prop, "yyyy-MM-dd'T'hh:mm:ss'Z'", {'zone': 'utc'});
    
                            const date = dt.toLocaleString(DateTime.DATE_MED)
                            const time = dt.toFormat('HHmm') + ' UTC ';
    
                            prop = time + " " + date;
                        }
    
                        if (key == 'f-scale') {
                            let scale;
                            if (tor_full['properties']['scale'] === undefined) {
                                scale = 'F'
                                let dt = DateTime.fromISO(tor_full['properties']['datetime-start']);
                                if (dt > DateTime.utc(2007, 2, 1, 0)) {
                                    scale = 'EF';
                                }
                            }
                            else {
                                scale = tor_full['properties']['scale'];
                            }
                            prop = scale + (prop == -9 ? 'U' : prop);
                            header_fscale = prop;
                        }
    
                        if (key == 'path-length' && prop !== undefined) {
                            if (info['units'] == 'km') {
                                prop = prop * 1.609; // Convert miles to kilometers
                            }
                            prop = Math.round(prop * 10) / 10;
                        }
    
                        if (key == 'path-width' && prop !== undefined) {
                            if (info['units'] == 'm') {
                                prop = prop * 0.9144; // Convert yards to meters
                            }
                            prop = Math.round(prop);
                        }

                        if (key == 'source') {
                            prop = prettysources(prop);
                        }

                        if (key.startsWith('comments')) {
                            let comment_key;
                            [key, comment_key] = key.split("_").slice(0, 2);
                            prop = tor_props[key][comment_key];
                        }
                    }

                    if (prop === undefined) {
                        continue;
                    }

                    table_rows.push("<tr><th>" + header + "</th>" + 
                                    "<td>" + prop + (info['units'] == '' ? '' : ' ' + info['units']) + "</tr>");
                }

                const dt = DateTime.fromFormat(tor_props['datetime-start'], "yyyy-MM-dd'T'hh:mm:ss'Z'", {'zone': 'utc'});

                const click_intv_start = dt.minus(this.props['cday_offset']).startOf('day').plus(this.props['cday_offset']);
                const click_intv_end = dt.minus(this.props['cday_offset']).endOf('day').plus(this.props['cday_offset'])
                                        .plus({'millisecond' : 1});
                const click_intv = Interval.fromDateTimes(click_intv_start, click_intv_end);
    
                const date = click_intv_start.toLocaleString(DateTime.DATE_FULL)

                const partitioner = this.props['partitioner'];
                const part_key_idx = partitioner.part_keys.map(pk => pk.slice(-2)).indexOf(header_fscale.slice(-2));
                const banner_color = colorBlend(partitioner.colors[part_key_idx] + '77', '#818888ff');
                const date_link = '<a id="popup-link" data-clickintv="' + click_intv.toISO() + '" href="#">' 
                + date + '</a>';
    
                const header = `<h1 style="background-color: ${banner_color}">
                                ${header_fscale} Tornado |
                                ${TORNADO_NAMES[tor_props['id']] === undefined ? "" : `: "${TORNADO_NAMES[tor_props['id']]}"`}
                                ${date_link}
                            </h1>`;

                const issue_link = "<a href='https://tornadoarchive.com/home/data-submission/?tornadoid=" + tor_props['id'] + "' target='blank_'>Report an issue</a>";
                const description = "<div class='tornado-popup'>" + header + "<div class='tornado-popup-stats'>" +
                                    "<table>" + table_rows.join("") + "</table></div>" + issue_link + "</div>";
                
                // Ensure that if the map is zoomed out such that multiple
                // copies of the feature are visible, the popup appears
                // over the copy being pointed to.
                while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
                }
                
                new Popup({'maxWidth': '400px'})
                            .setLngLat(coordinates)
                            .setHTML(description)
                            .addTo(map);

                const isStyleRule = (obj: any) : obj is CSSStyleRule => {
                    return obj.constructor.name == 'CSSStyleRule';
                }

                let found = false;

                for (let ss of document.styleSheets) {
                    let rules;
                    try {
                        rules = ss.cssRules;
                    }
                    catch {
                        continue;
                    }

                    for (let rule of rules) {
                        if (isStyleRule(rule) && rule.selectorText.includes('.maplibregl-popup-anchor-top') && rule.selectorText.includes('.maplibregl-popup-tip')) {
                            rule.style.borderBottomColor = banner_color;
                            found = true;
                        }
                    }
                }
                
                if (!found) {
                    document.styleSheets[1].insertRule('.maplibregl-popup-anchor-top .maplibregl-popup-tip { border-bottom-color: ' + banner_color + '; }');
                }

                let link_elem = document.getElementById('popup-link');

                if (link_elem !== null) {
                    const le = link_elem;
                    link_elem.addEventListener('click', ev => {
                        ev.preventDefault();
                        if (this.props['onintervalchange'] !== undefined && le.dataset !== undefined && le.dataset.clickintv !== undefined) {
                            const intv = Interval.fromISO(le.dataset.clickintv, {'zone': 'utc'});
                            this.props['onintervalchange'](new SelectionInterval(intv), true);
                        }
                    })
                }
            });

            if (!layer_id.includes('polys')) {
                // Set mouse behavior for this layer
                map.on('mouseenter', layer_id, event => {
                    map.getCanvas().style.cursor = 'pointer';
                });

                map.on('mouseleave', layer_id, event => {
                    map.getCanvas().style.cursor = '';
                });
            }
        }

        let poly_layers = Object.fromEntries(Object.entries(layers).filter(([layer_id, layer_json]) => layer_id.includes('polys')));
        layers = Object.fromEntries(Object.entries(layers).filter(([layer_id, layer_json]) => !layer_id.includes('polys')));

        Object.entries(poly_layers).forEach(add_layer);
        Object.entries(layers).forEach(add_layer);
    }

    async torsToLayers() {
        let tors: Record<string, TornadoGeoJSONMin> | null = null;
        if (this.props['tor_coll'] !== null && this.props['interval'] !== null) {
            tors = await this.props['tor_coll'].getGeoJSON(this.props['interval'], this.partitioner);
        }

        return torsToLayers(tors, this.partitioner);
    }

    screenshot() {
        const takeScreenshot = (map: Map) : Promise<HTMLCanvasElement> => {
            return new Promise((resolve, reject) => {
                map.once('render', () => {
                    let canvas = document.createElement('canvas');
                    const map_canvas = map.getCanvas();

                    canvas.width = map_canvas.width;
                    canvas.height = map_canvas.height;

                    let ctx = canvas.getContext('2d');
                    if (ctx === null) {
                        reject("Could not get a drawing context in the map screenshot function.");
                        return;
                    }

                    ctx.drawImage(map_canvas, 0, 0);
                    resolve(canvas);
                });

                // Trigger a render
                map.setBearing(map.getBearing());
            });
        }

        if (this.map === null) {
            throw "Map object is null in the map widget screenshot function";
        }

        return takeScreenshot(this.map);
    }

    render() {
        return (
            <div ref={el => this.mapContainer = el} className="mapContainer" />
        )
    }
}

export default MapWidget;
